import React, { useState, type SetStateAction, type Dispatch } from 'react';
import { IDocument } from '../Interfaces/IDocument';
import { IDraftMessageFullObject } from '../Interfaces/IDraftMessage';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Grid,
  Typography,
} from '@mui/material';
import { ViewDraftMessage } from './DraftMessage/ViewDraftMessage';
import { NewDraftMessage } from './DraftMessage/NewDraftMessage';
import { DraftDocumentOverview } from './DraftMessage/DraftDocumentOverview';
import { FSDirectorySearch } from '../FSDirectory/FSDirectorySearch';
import { ILegalTransactionRecordNumber } from '../Interfaces/ILegalTransactionRecordNumber';
import { ILegalTransactionTransactionNumber } from '../Interfaces/ILegalTransactionTransactionNumber';
import { GridExpandMoreIcon } from '@mui/x-data-grid';
import { ILegalClient } from '../Interfaces/ILegalClient';
import { DeedGenerationPanel } from './DeedGenerationPanel';
import { useGetWordPluginStatus } from '../hooks/reactQuerys/WordPluginAccessQueries';

interface IProps {
  draftArray: IDocument[];
  setDraftArray: Dispatch<SetStateAction<IDocument[]>>;
  draftMessageArray: IDraftMessageFullObject[];
  setDraftMessageArray: Dispatch<SetStateAction<IDraftMessageFullObject[]>>;
  hasDirDraft: boolean;
  recordNumberArray: ILegalTransactionRecordNumber[];
  transactionNumberArray: ILegalTransactionTransactionNumber[];
  assignedLegalClients: ILegalClient[];
  idLegalTransaction: number;
  idLegalTransactionType?: number | null;
}

export const LegalTransactionDraftOverview: React.FC<IProps> = props => {
  const [idDraftMessageParent, setIdDraftMessageParent] = useState<
    number | null
  >(null);
  const { isWordPluginEnabled } = useGetWordPluginStatus();

  return (
    <>
      {/*
        Der Entwurf ist der Ort, an dem die Urkunde entsteht — deshalb steht der
        Einstieg hier und nicht in der Verwaltung (#1245). Nur fuer Benutzer, fuer
        die das Word-Plugin freigeschaltet ist (/admin/dev/word-plugin).
      */}
      {isWordPluginEnabled && (
        <DeedGenerationPanel
          idLegalTransaction={props.idLegalTransaction}
          idLegalTransactionType={props.idLegalTransactionType}
          // »In DiNo erstellen«: wie ein hochgeladener Entwurf — neue, negative Kennung, gespeichert
          // mit dem Vorgang (#1245, Teil 48).
          onDraftCreated={document =>
            props.setDraftArray(list => [
              ...list,
              {
                ...document,
                idDocument:
                  Math.min(0, ...list.map(entry => entry.idDocument)) - 1,
              },
            ])
          }
        />
      )}
      <DraftDocumentOverview
        draftArray={props.draftArray}
        setDraftArray={props.setDraftArray}
        assignedLegalClients={props.assignedLegalClients}
      />
      {props.hasDirDraft && (
        <Accordion sx={{ mb: 5 }}>
          <AccordionSummary expandIcon={<GridExpandMoreIcon />}>
            Ordnersuche
          </AccordionSummary>
          <AccordionDetails>
            <FSDirectorySearch
              key='draft-fs-search'
              searchArray={props.recordNumberArray
                .filter(recordNumber => recordNumber.RecordNumberEntry !== '')
                .map(recordNumber => recordNumber.RecordNumberEntry)}
              searchArrayFallback={props.transactionNumberArray
                .filter(
                  transactionNumber =>
                    transactionNumber.TransactionNumberEntry !== ''
                )
                .map(
                  transactionNumber => transactionNumber.TransactionNumberEntry
                )}
              searchItem='draft'
              documentArray={props.draftArray}
              setDocumentArray={props.setDraftArray}
            />
          </AccordionDetails>
        </Accordion>
      )}

      <Typography variant='h5' sx={{ mb: 3 }}>
        Kommentare
        <NewDraftMessage
          draftMessageArray={props.draftMessageArray}
          setDraftMessageArray={props.setDraftMessageArray}
          idDraftMessageParent={idDraftMessageParent}
          setIdDraftMessageParent={setIdDraftMessageParent}
        />
      </Typography>
      <Grid container spacing={2}>
        {props.draftMessageArray.length === 0 && (
          <Grid item xs={12}>
            <>--- Keine Kommentare vorhanden ---</>
          </Grid>
        )}

        {props.draftMessageArray.map(x => (
          <Grid item xs={12}>
            <ViewDraftMessage
              key={x.idDraftMessage}
              draftMessageArray={props.draftMessageArray}
              setDraftMessageArray={props.setDraftMessageArray}
              draftMessageObject={x}
              setIdDraftMessageParent={setIdDraftMessageParent}
              enableStateEdit
              assignedLegalClients={props.assignedLegalClients}
            />
          </Grid>
        ))}
      </Grid>
    </>
  );
};
